import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";
import { technologyDetails } from "@/mocks/technologyDetailsData";

export default function TechnologyDetailPage({ slug }: { slug: string }) {
  const tech = technologyDetails.find((t) => t.slug === slug);

  if (!tech) return null;

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Technologies", href: "/technologies" },
          { label: tech.name },
        ]}
      />

      <PageHero
        badge={tech.hero.badge}
        headline={tech.hero.headline}
        subtext={tech.hero.subtext}
        primaryCTA={tech.hero.primaryCTA}
        secondaryCTA={tech.hero.secondaryCTA}
      />

      {/* Overview */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src={tech.overview.image}
                  alt={tech.name}
                  className="w-full h-[300px] md:h-[400px] object-cover object-top"
                />
              </div>
            </AnimatedSection>
            <div>
              <AnimatedSection delay={100}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse-dot" />
                  <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">
                    {tech.name} Overview
                  </span>
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                  {tech.overview.title}
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed">
                  {tech.overview.text}
                </p>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Use It */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Why We Use {tech.name}
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {tech.whyWeUse.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                {tech.name} Capabilities
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {tech.capabilities.map((cap, i) => (
              <AnimatedSection key={cap.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${cap.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{cap.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cap.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology Stack
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {tech.stack.map((item, i) => (
              <AnimatedSection key={item.name} delay={i * 60}>
                <div className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift h-full">
                  {item.logo ? (
                    <img src={item.logo} alt={item.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${item.icon} text-2xl`} />
                    </span>
                  )}
                  <span className="text-sm font-semibold text-foreground-800 text-center">{item.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Architecture
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              {tech.architecture.map((layer, i) => (
                <AnimatedSection key={layer.name} delay={i * 120}>
                  <div className="relative">
                    {i > 0 && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-gradient-to-b from-transparent to-accent-500/40" />
                    )}
                    <div className="flex items-center gap-4 px-6 py-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 w-[280px] md:w-[380px]">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                        <i className={`${layer.icon} text-lg`} />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 font-heading">
                          {layer.name}
                        </h4>
                        <p className="text-xs text-foreground-500">{layer.desc}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Business Benefits
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {tech.benefits.map((benefit, i) => (
              <AnimatedSection key={benefit.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${benefit.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{benefit.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Related Services
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {tech.relatedServices.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <a
                  href={service.href}
                  className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full block"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{service.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 group-hover:text-accent-700 transition-colors btn-arrow-hover">
                    Explore
                    <i className="ri-arrow-right-line btn-arrow text-xs transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <FAQSection heading={`${tech.name} FAQ`} items={tech.faq} />

      <PageCTA
        heading={`Ready to Build with ${tech.name}?`}
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}